'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { buildLocalePath, type Locale } from '@/i18n/config';

export interface FooterLink {
  label: string;
  href: string;
  target?: string;
  rel?: string;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface PublicFooterProps {
  tenantName?: string;
  sections?: FooterSection[];
  copyright?: string;
}

const SPONSORS = [
  {
    src: '/images/uwr/sponsors/MotoGPAus2026.webp',
    alt: 'MotoGP Australian Motorcycle Grand Prix',
  },
  { src: '/images/uwr/sponsors/QuadLock.png', alt: 'Quad Lock' },
  { src: '/images/uwr/sponsors/Michelin.png', alt: 'Michelin' },
  { src: '/images/uwr/sponsors/Allianz.png', alt: 'Allianz Partners' },
];

interface FooterSectionConfig {
  titleKey: 'rideInformation' | 'support';
  links: { labelKey: string; href: string; external?: boolean }[];
}

const SECTION_CONFIGS: FooterSectionConfig[] = [
  {
    titleKey: 'rideInformation',
    links: [
      { labelKey: 'aboutUwr', href: '/about' },
      { labelKey: 'salvationArmy', href: '/about/the-salvation-army' },
      { labelKey: 'prizes', href: '/about/prizes' },
      { labelKey: 'ourPartners', href: '/about/partners' },
    ],
  },
  {
    titleKey: 'support',
    links: [
      { labelKey: 'faqs', href: '/about/faq' },
      { labelKey: 'safetyInformation', href: '/about/safety-information' },
      { labelKey: 'contactUs', href: '/contact' },
      { labelKey: 'termsOfService', href: '/terms' },
      {
        labelKey: 'privacyPolicy',
        href: 'https://www.harley-davidson.com/au/en/footer/utility/privacy-policy.html',
        external: true,
      },
    ],
  },
];

export function PublicFooter({
  tenantName = 'United We Ride',
  sections: sectionsProp,
  copyright,
}: PublicFooterProps) {
  const locale = useLocale() as Locale;
  const tFooter = useTranslations('footer');
  const tUwrFooter = useTranslations('uwrFooter');
  const currentYear = new Date().getFullYear();

  const sections: FooterSection[] =
    sectionsProp ??
    SECTION_CONFIGS.map((config) => ({
      title: tUwrFooter(config.titleKey),
      links: config.links.map((link) => ({
        label: tUwrFooter(link.labelKey as 'aboutUwr'),
        href: link.external ? link.href : buildLocalePath(locale, link.href),
        ...(link.external && { target: '_blank', rel: 'noopener noreferrer' }),
      })),
    }));

  return (
    <footer className="bg-stone-100 text-stone-700">
      {/* Sponsor strip */}
      <div className="container py-12 sm:py-16">
        <div className="grid gap-8 md:grid-cols-[auto,1fr] md:items-start md:gap-12">
          {/* Proudly Supporting */}
          <div className="flex flex-col">
            <h3 className="font-bold text-base text-stone-900">
              {tUwrFooter('proudlySupporting')}
            </h3>
            <div className="mt-5 flex items-center h-20">
              <Image
                src="/images/uwr/SalvationArmyLogo.svg"
                alt="The Salvation Army"
                width={120}
                height={143}
                className="h-20 w-auto"
              />
            </div>
          </div>

          {/* Supported by */}
          <div className="flex flex-col md:border-l md:border-stone-200 md:pl-12">
            <h3 className="font-bold text-base text-stone-900">{tUwrFooter('supportedBy')}</h3>
            <div className="mt-5 grid grid-cols-2 gap-6 sm:grid-cols-4 sm:gap-8">
              {SPONSORS.map((sponsor) => (
                <div key={sponsor.src} className="flex items-center justify-center h-20">
                  <Image
                    src={sponsor.src}
                    alt={sponsor.alt}
                    width={160}
                    height={64}
                    className="max-h-14 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-stone-200" />

      {/* Socials + nav columns */}
      <div className="container py-12">
        <div className="grid gap-10 md:grid-cols-[auto,1fr,1fr]">
          <div className="flex items-start gap-4">
            <a
              href="https://facebook.com/harleyaustralia"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-800 hover:text-uwr-orange transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="h-6 w-6" />
            </a>
            <a
              href="https://instagram.com/harleyaustralia"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-800 hover:text-uwr-orange transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-6 w-6" />
            </a>
            <a
              href="https://youtube.com/user/HarleyDavidsonAus"
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-800 hover:text-uwr-orange transition-colors"
              aria-label="YouTube"
            >
              <Youtube className="h-6 w-6" />
            </a>
          </div>

          {sections.map((section) => (
            <div key={section.title}>
              <h3 className="font-bold text-base text-stone-900">{section.title}</h3>
              <ul className="mt-4 space-y-2">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      prefetch={false}
                      href={link.href}
                      target={link.target}
                      rel={link.rel}
                      className="text-sm text-stone-600 hover:text-uwr-orange transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-stone-200" />

      <div className="container py-6">
        <p className="text-xs text-stone-500">
          {copyright || tFooter('copyright', { year: currentYear, tenantName })}
        </p>
      </div>
    </footer>
  );
}
